import type { TFunction } from 'i18next';
import type { IMatchScheduleItem } from '../../types/schedule';
import type { ScheduleConflict, ScheduleConflictKind } from './scheduleConflictDetector';

const SCHEDULE_CONFLICT_I18N_PREFIX = 'creativeDetail-';

const MERGEABLE_OVERLAP_KINDS: ScheduleConflictKind[] = [
  'PLAYER_OVERLAP',
  'TEAM_OVERLAP',
  'COURT_OVERLAP',
];

const KIND_PRECEDENCE: Record<string, number> = {
  PLAYER_OVERLAP: 0,
  TEAM_OVERLAP: 1,
  COURT_OVERLAP: 2,
};

export function incidentKey(idA: number, idB: number, startMs: number): string {
  const [lo, hi] = idA < idB ? [idA, idB] : [idB, idA];
  return `${lo}-${hi}-${startMs}`;
}

function overlapStartMsFromConflict(conflict: ScheduleConflict): number {
  const v = conflict.messageParams.overlapStartMs;
  return typeof v === 'number' ? v : 0;
}

function isMergeableOverlap(conflict: ScheduleConflict): boolean {
  return MERGEABLE_OVERLAP_KINDS.includes(conflict.kind);
}

function primaryKind(kinds: ScheduleConflictKind[]): ScheduleConflictKind {
  return [...kinds].sort((a, b) => (KIND_PRECEDENCE[a] ?? 99) - (KIND_PRECEDENCE[b] ?? 99))[0];
}

function mergeBucket(bucket: ScheduleConflict[]): ScheduleConflict {
  const kinds = [...new Set(bucket.map((c) => c.kind))] as ScheduleConflictKind[];
  const primary = primaryKind(kinds);
  const [idA, idB] = bucket[0].matchIds;
  const startMs = overlapStartMsFromConflict(bucket[0]);
  const key = incidentKey(idA, idB, startMs);

  const entityLabels = new Set<string>();
  for (const c of bucket) {
    if (c.entityLabel) entityLabels.add(c.entityLabel);
  }

  const template = bucket.find((c) => c.kind === primary) ?? bucket[0];
  const mergedKinds = kinds.length > 1 ? kinds : undefined;

  return {
    ...template,
    id: `${primary}-incident-${key}`,
    kind: primary,
    entityLabel: [...entityLabels].join(', '),
    messageParams: {
      ...template.messageParams,
      entityLabel: [...entityLabels].join(', '),
      overlapStartMs: startMs,
      ...(mergedKinds ? { mergedKinds: mergedKinds.join(',') } : {}),
    },
  };
}

export function mergeOverlapsForDisplay(conflicts: ScheduleConflict[]): ScheduleConflict[] {
  const buckets = new Map<string, ScheduleConflict[]>();

  for (const conflict of conflicts) {
    if (!isMergeableOverlap(conflict)) {
      continue;
    }
    const [idA, idB] = conflict.matchIds;
    const startMs = overlapStartMsFromConflict(conflict);
    const key = incidentKey(idA, idB, startMs);
    const list = buckets.get(key) ?? [];
    list.push(conflict);
    buckets.set(key, list);
  }

  const mergedByKey = new Map<string, ScheduleConflict>();
  for (const [key, bucket] of buckets) {
    mergedByKey.set(key, bucket.length === 1 ? bucket[0] : mergeBucket(bucket));
  }

  const emittedKeys = new Set<string>();
  const result: ScheduleConflict[] = [];
  for (const conflict of conflicts) {
    if (!isMergeableOverlap(conflict)) {
      result.push(conflict);
      continue;
    }
    const [idA, idB] = conflict.matchIds;
    const startMs = overlapStartMsFromConflict(conflict);
    const key = incidentKey(idA, idB, startMs);
    if (emittedKeys.has(key)) continue;
    emittedKeys.add(key);
    result.push(mergedByKey.get(key)!);
  }
  return result;
}

export function parseMergedKinds(
  messageParams: Record<string, string | number>
): ScheduleConflictKind[] | null {
  const raw = messageParams.mergedKinds;
  if (typeof raw !== 'string' || !raw) return null;
  return raw.split(',').filter(Boolean) as ScheduleConflictKind[];
}

export function formatScheduleConflictMessage(
  conflict: ScheduleConflict,
  matchA: IMatchScheduleItem | undefined,
  matchB: IMatchScheduleItem | undefined,
  formatMatchSlot: (match: IMatchScheduleItem | undefined) => string,
  t: TFunction
): string {
  const slotA = formatMatchSlot(matchA);
  const slotB = formatMatchSlot(matchB);

  if (conflict.kind === 'MISSING_END') {
    return t(SCHEDULE_CONFLICT_I18N_PREFIX + conflict.messageKey, {
      matchId: conflict.messageParams.matchId,
    });
  }

  if (
    conflict.kind === 'BRACKET_ORDERING' ||
    conflict.kind === 'BOX_ROUND_ORDERING' ||
    conflict.kind === 'PARENT_CLASS_ORDERING'
  ) {
    return t(SCHEDULE_CONFLICT_I18N_PREFIX + conflict.messageKey, {
      ...conflict.messageParams,
      slotA,
      slotB,
    });
  }

  return t(SCHEDULE_CONFLICT_I18N_PREFIX + conflict.messageKey, {
    entityLabel: conflict.entityLabel,
    slotA,
    slotB,
  });
}
